import React, { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { filasProyeccion, planSemana } from "@/lib/proyeccionMensual";
import { semaforoColor, ACTUAL_COLOR } from "@/lib/reporteCalc";
import { kgFmt, fmtKg, valor, rango, th, td, SEP } from "@/components/control/ProyeccionTabla";

const sk = (w) => `${w.year}-${w.week}`;
const DIF_HEAD = "#7A1E2C";
const difCls = (x) => (x == null || Math.abs(x) < 0.005 ? "" : x < 0 ? "text-red-700" : "text-emerald-700");

function Fila({ r, plan, wReal, wPlan }) {
  const ip = plan.w(wPlan), ir = r.w(wReal);
  const kp = ip == null || !wPlan.kg ? null : ip / wPlan.kg;
  const kr = ir == null || !wReal.kg ? null : ir / wReal.kg;
  const dk = kp == null || kr == null ? null : kr - kp;
  const di = ip == null || ir == null ? null : ir - ip;
  return (
    <tr className={`border-t border-slate-100 font-bold ${r.total ? "bg-[var(--gf-total)]" : ""}`} data-testid={`cs-cmp-row-${r.k}`}>
      <td className="px-3 py-1.5 whitespace-nowrap"><span className="inline-block w-7 text-[10px] text-slate-500">{r.b || ""}</span>{r.label}</td>
      <td className={`${td} ${SEP} font-normal`} data-testid={`cmp-${r.k}-plan-kg`}>{fmtKg(r, kp)}</td>
      <td className={td} data-testid={`cmp-${r.k}-plan-imp`}>{valor(r, ip)}</td>
      <td className={`${td} ${SEP} font-normal`} data-testid={`cmp-${r.k}-real-kg`}>{fmtKg(r, kr)}</td>
      <td className={td} data-testid={`cmp-${r.k}-real-imp`}>{valor(r, ir)}</td>
      <td className={`${td} ${SEP} font-normal ${difCls(dk)}`} data-testid={`cmp-${r.k}-dif-kg`}>{fmtKg(r, dk)}</td>
      <td className={`${td} ${difCls(di)}`} data-testid={`cmp-${r.k}-dif-imp`}>{valor(r, di)}</td>
    </tr>
  );
}

// Anual: una semana completa (domingo a sábado) consolidada; Plan con METAS de cada mes.
export function ComparativoAnual({ semanas, rows, onWeekClick, estados, actual }) {
  const [sel, setSel] = useState(null);
  if (!semanas.length) return <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500" data-testid="cmp-vacio">No hay semanas para el año seleccionado.</p>;
  const w = semanas.find((x) => sk(x) === sel) || semanas[semanas.length - 1];
  const wPlan = { ...w, plan: true, kg: w.planKg };
  const head = (label, color) => (
    <th colSpan={2} className={`${th} ${SEP} cursor-pointer hover:brightness-110`} style={{ backgroundColor: (actual === sk(w) && ACTUAL_COLOR) || (estados && semaforoColor(estados[sk(w)])) || color }} onClick={() => onWeekClick(w)} title="Ver desglose de la semana" data-testid={`cmp-head-${label.toLowerCase()}`} data-semaforo={estados ? (actual === sk(w) ? "actual" : String(estados[sk(w)] ?? "sin_dato")) : undefined}>
      {label}<div className="font-normal normal-case">Semana {w.week} · {w.sub}</div>
    </th>
  );
  const sub = () => [0, 1, 2].map((i) => <React.Fragment key={i}><td className={`text-center text-[10px] text-slate-500 ${SEP}`}>$/kg</td><td className="text-center text-[10px] text-slate-500">Importe</td></React.Fragment>);
  const dKg = w.kg - wPlan.kg;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Semana</span>
        <Select value={sk(w)} onValueChange={setSel}>
          <SelectTrigger className="w-64" data-testid="cmp-semana-select"><SelectValue /></SelectTrigger>
          <SelectContent>{[...semanas].reverse().map((x) => <SelectItem key={sk(x)} value={sk(x)} data-testid={`cmp-semana-${x.week}`}>Semana {x.week} · {x.sub}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-500">Plan con METAS de Gestión Planta · Real con lo capturado en la semana · Diferencia = Real − Plan · Clic en el encabezado para ver el desglose</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="cmp-tabla">
        <table className="w-full text-sm">
          <thead><tr><th className={`${th} bg-[var(--gf-head)] text-left`}>Fechas</th>{head("Plan", "#003B73")}{head("Real", "var(--gf-head)")}{head("Diferencia", DIF_HEAD)}</tr></thead>
          <tbody>
            <tr className="border-t border-slate-100 bg-[var(--gf-info)]">
              <td className="px-3 py-1.5 text-xs font-semibold text-slate-600">DÍAS</td>
              {[0, 1, 2].map((i) => <td key={i} colSpan={2} className={`px-3 py-1.5 text-center font-bold ${SEP}`} data-testid={`cmp-dias-${i}`}>{w.dias}</td>)}
            </tr>
            <tr className="border-t border-slate-100 font-bold" data-testid="cmp-row-venta">
              <td className="px-3 py-1.5"><span className="inline-block w-7" />VENTA EN KILOS</td>
              <td colSpan={2} className={`${td} ${SEP}`} data-testid="cmp-venta-plan">{kgFmt(wPlan.kg)}</td>
              <td colSpan={2} className={`${td} ${SEP}`} data-testid="cmp-venta-real">{kgFmt(w.kg)}</td>
              <td colSpan={2} className={`${td} ${SEP} ${difCls(dKg)}`} data-testid="cmp-venta-dif">{kgFmt(dKg)}</td>
            </tr>
            <tr><td />{sub()}</tr>
            {rows.map((r) => (r.gap
              ? <tr key={`gap${r.gap}`} aria-hidden><td colSpan={7} style={{ height: 22, padding: 0 }} /></tr>
              : <React.Fragment key={r.k}>
                  {r.total && <tr aria-hidden><td colSpan={7} style={{ height: 10, padding: 0 }} /></tr>}
                  <Fila r={r} plan={r} wReal={w} wPlan={wPlan} />
                </React.Fragment>))}
          </tbody>
        </table>
      </div>
      {!wPlan.kg && <p className="text-xs font-semibold text-amber-700" data-testid="cmp-sin-meta">Sin META de ventas capturada en Gestión Planta para esta semana: el plan se muestra en cero.</p>}
    </div>
  );
}

export function ComparativoSemanal({ filas, rows, ctx }) {
  const reales = filas.filter((w) => w.real);
  const [sel, setSel] = useState(null);
  if (!reales.length) {
    return <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500" data-testid="cmp-vacio">Aún no hay semanas con venta real capturada en este mes.</p>;
  }
  const w = reales.find((x) => sk(x) === sel) || reales[reales.length - 1];
  const { fila: wPlan, planVars } = planSemana(w, ctx.vars, ctx.ym);
  const planRows = Object.fromEntries(filasProyeccion({ ...ctx, filas: [wPlan], vars: planVars }).filter((r) => !r.gap).map((r) => [r.k, r]));
  const head = (label, color) => <th colSpan={2} className={`${th} ${SEP}`} style={{ backgroundColor: color }}>{label}<div className="font-normal normal-case">Semana {w.week} · {rango(w, ctx.ym)}</div></th>;
  const sub = () => [0, 1, 2].map((i) => <React.Fragment key={i}><td className={`text-center text-[10px] text-slate-500 ${SEP}`}>$/kg</td><td className="text-center text-[10px] text-slate-500">Importe</td></React.Fragment>);
  const dKg = wPlan.kg == null ? null : w.kg - wPlan.kg;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Semana</span>
        <Select value={sk(w)} onValueChange={setSel}>
          <SelectTrigger className="w-64" data-testid="cmp-semana-select"><SelectValue /></SelectTrigger>
          <SelectContent>{reales.map((x) => <SelectItem key={sk(x)} value={sk(x)} data-testid={`cmp-semana-${x.week}`}>Semana {x.week} · {rango(x, ctx.ym)}</SelectItem>)}</SelectContent>
        </Select>
        <span className="text-xs text-slate-500">Plan con METAS de Gestión Planta · Real con lo capturado en la semana · Diferencia = Real − Plan</span>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white" data-testid="cmp-tabla">
        <table className="w-full text-sm">
          <thead><tr><th className={`${th} bg-[var(--gf-head)] text-left`}>Fechas</th>{head("Plan", "#003B73")}{head("Real", "var(--gf-head)")}{head("Diferencia", DIF_HEAD)}</tr></thead>
          <tbody>
            <tr className="border-t border-slate-100 bg-[var(--gf-info)]">
              <td className="px-3 py-1.5 text-xs font-semibold text-slate-600">DÍAS</td>
              {[0, 1, 2].map((i) => <td key={i} colSpan={2} className={`px-3 py-1.5 text-center font-bold ${SEP}`} data-testid={`cmp-dias-${i}`}>{w.dias}</td>)}
            </tr>
            <tr className="border-t border-slate-100 font-bold" data-testid="cmp-row-venta">
              <td className="px-3 py-1.5"><span className="inline-block w-7" />VENTA EN KILOS</td>
              <td colSpan={2} className={`${td} ${SEP}`} data-testid="cmp-venta-plan">{kgFmt(wPlan.kg)}</td>
              <td colSpan={2} className={`${td} ${SEP}`} data-testid="cmp-venta-real">{kgFmt(w.kg)}</td>
              <td colSpan={2} className={`${td} ${SEP} ${difCls(dKg)}`} data-testid="cmp-venta-dif">{kgFmt(dKg)}</td>
            </tr>
            <tr><td />{sub()}</tr>
            {rows.map((r) => (r.gap
              ? <tr key={`gap${r.gap}`} aria-hidden><td colSpan={7} style={{ height: 22, padding: 0 }} /></tr>
              : <React.Fragment key={r.k}>
                  {r.total && <tr aria-hidden><td colSpan={7} style={{ height: 10, padding: 0 }} /></tr>}
                  <Fila r={r} plan={planRows[r.k]} wReal={w} wPlan={wPlan} />
                </React.Fragment>))}
          </tbody>
        </table>
      </div>
      {ctx.vars?.ventas?.meta == null && <p className="text-xs font-semibold text-amber-700" data-testid="cmp-sin-meta">Sin META de ventas capturada en Gestión Planta: el plan no se puede calcular.</p>}
    </div>
  );
}
